import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Wallet,
  Plus,
  Calendar,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
  Download,
  Filter,
  Search,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePartner } from '../contexts/PartnerContext';
import { PartnerWithdrawal } from '../api/client';
import TierRestriction from './TierRestriction';
import BlurredAmount from './BlurredAmount';

interface CreateWithdrawalFormData {
  usdtWallet: string;
  amount: string;
}

const Payments: React.FC = () => {
  const navigate = useNavigate();
  const { 
    partner, 
    withdrawals, 
    withdrawalStats,
    createWithdrawal,
    cancelWithdrawal,
    refreshWithdrawals,
    loading,
    isAuthenticated
  } = usePartner();
  
  const [showCreateWithdrawal, setShowCreateWithdrawal] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateWithdrawalFormData>({
    usdtWallet: '',
    amount: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      refreshWithdrawals();
    }
  }, [isAuthenticated, refreshWithdrawals]);

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-400" />;
      case 'rejected':
        return <XCircle size={16} className="text-red-400" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Выполнена';
      case 'pending':
        return 'Ожидает обработки';
      case 'rejected':
        return 'Отклонена';
      default:
        return 'Неизвестно';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'pending':
        return 'text-yellow-400';
      case 'rejected':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateWithdrawal = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setCreateLoading(true);

    try {
      const amount = parseFloat(createFormData.amount);
      if (isNaN(amount) || amount < 1000) {
        throw new Error('Минимальная сумма для вывода 1000 рублей');
      }

      await createWithdrawal(createFormData.usdtWallet, amount);
      
      // Сброс формы
      setCreateFormData({ usdtWallet: '', amount: '' });
      setShowCreateWithdrawal(false);
      
      // Показываем уведомление об успехе
      showNotification('success', 'Заявка на выплату успешно создана!');
    } catch (error: any) {
      setCreateError(error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancelWithdrawal = async (withdrawalId: string) => {
    if (window.confirm('Вы уверены, что хотите отменить эту заявку на выплату?')) {
      try {
        await cancelWithdrawal(withdrawalId);
        // Показываем уведомление об успехе
        showNotification('success', 'Заявка на выплату успешно отменена!');
      } catch (error: any) {
        // Ошибка при отмене заявки
        // Показываем уведомление об ошибке
        showNotification('error', 'Ошибка при отмене заявки на выплату');
      }
    }
  };

  const filteredWithdrawals = withdrawals.filter(withdrawal => {
    const matchesStatus = filterStatus === 'all' || withdrawal.status === filterStatus;
    const matchesSearch = searchQuery === '' || 
      withdrawal.usdtWallet.toLowerCase().includes(searchQuery.toLowerCase()) ||
      withdrawal.amount.includes(searchQuery);
    
    return matchesStatus && matchesSearch;
  });

  const availableBalance = partner?.balance || '0';
  const lastWithdrawal = withdrawals.find(w => w.status === 'completed');
  const lastWithdrawalAmount = lastWithdrawal ? lastWithdrawal.amount : '0';
  const lastWithdrawalDate = lastWithdrawal ? lastWithdrawal.createdAt : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-800 rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tesla-bg text-white pt-20 px-6 py-8">
      {/* Уведомления */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
            notification.type === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <XCircle size={20} />
            )}
            <span>{notification.message}</span>
          </div>
        </motion.div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Заголовок */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg tesla-glass hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft size={20} className="text-white" />
              </motion.button>
              <div className="flex items-center space-x-3">
                <Wallet size={24} className="text-blue-400" />
                <h1 className="text-3xl font-bold text-white">Платежи и выплаты</h1>
              </div>
            </div>
            
            {/* Кнопка создания выплаты в заголовке */}
            <motion.button
              onClick={() => setShowCreateWithdrawal(true)}
              disabled={parseFloat(availableBalance) < 5000}
              className="tesla-button flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: parseFloat(availableBalance) >= 5000 ? 1.02 : 1 }}
              whileTap={{ scale: parseFloat(availableBalance) >= 5000 ? 0.98 : 1 }}
            >
              <Plus size={16} />
              <span>Создать выплату</span>
            </motion.button>
          </div>
          
          {parseFloat(availableBalance) < 5000 && (
            <div className="mb-6">
              <p className="text-red-400 text-sm">
                Недостаточно средств для вывода. Минимум: 5000 ₽
              </p>
            </div>
          )}

          {/* Balance Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Доступно к выводу */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="tesla-card p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <DollarSign size={20} className="text-green-400" />
                <h3 className="text-lg font-semibold text-white">Доступно к выводу</h3>
              </div>
              <BlurredAmount 
                 amount={availableBalance}
                 className="text-3xl font-bold text-green-400"
                 formatCurrency={true}
               />
              <p className="text-tesla-gray text-sm mt-2">Минимум для вывода: 1000 ₽</p>
            </motion.div>



            {/* Последняя выплата */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="tesla-card p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <Calendar size={20} className="text-purple-400" />
                <h3 className="text-lg font-semibold text-white">Последняя выплата</h3>
              </div>
              <BlurredAmount 
                 amount={lastWithdrawalAmount}
                 className="text-2xl font-bold text-white"
                 formatCurrency={true}
               />
              <p className="text-tesla-gray text-sm mt-2">
                {lastWithdrawalDate ? formatDate(lastWithdrawalDate) : 'Нет выплат'}
              </p>
            </motion.div>
          </div>
        </motion.div>



        {/* Create Withdrawal Form */}
        {showCreateWithdrawal && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="tesla-card p-6 mb-8"
          >
            <h3 className="text-xl font-semibold text-white mb-6">Создать выплату</h3>
            <form onSubmit={handleCreateWithdrawal} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-tesla-gray text-sm font-medium mb-2">
                    USDT TRC20 кошелек
                  </label>
                  <input
                    type="text"
                    value={createFormData.usdtWallet}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, usdtWallet: e.target.value }))}
                    placeholder="TRC20WalletAddress123456789"
                    className="w-full tesla-input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-tesla-gray text-sm font-medium mb-2">
                    Сумма в рублях
                  </label>
                  <input
                    type="number"
                    value={createFormData.amount}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="1000"
                    min="1000"
                    step="100"
                    className="w-full tesla-input"
                    required
                  />
                  <p className="text-tesla-gray text-xs mt-1">
                    Минимум: 1000 ₽
                  </p>
                </div>
              </div>

              {createError && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {createError}
                </div>
              )}

              <div className="flex space-x-4">
                <motion.button
                  type="submit"
                  disabled={createLoading || !createFormData.amount || parseFloat(createFormData.amount) < 1000}
                  className="tesla-button flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {createLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  ) : (
                    <Plus size={16} />
                  )}
                  <span>{createLoading ? 'Создание...' : 'Создать выплату'}</span>
                </motion.button>

                <button
                  type="button"
                  onClick={() => setShowCreateWithdrawal(false)}
                  className="tesla-button-secondary"
                >
                  Отмена
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Payment History */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="tesla-card p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-white flex items-center">
              <Calendar size={20} className="mr-2 text-blue-400" />
              История выплат
            </h3>
            <div className="flex space-x-3">
              {/* Поиск */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tesla-gray" />
                <input
                  type="text"
                  placeholder="Поиск по кошельку..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="tesla-input pl-10 pr-4 w-64"
                />
              </div>
              
              {/* Фильтр по статусу */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="tesla-input"
              >
                <option value="all">Все статусы</option>
                <option value="pending">Ожидает обработки</option>
                <option value="completed">Выполнена</option>
                <option value="rejected">Отклонена</option>
              </select>
            
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Сумма
                  </th>
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Кошелек
                  </th>
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Создана
                  </th>
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Обработана
                  </th>
                  <th className="text-left py-2 text-tesla-gray text-xs font-medium uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredWithdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-6 text-tesla-gray text-sm">
                      {withdrawals.length === 0 ? 'Нет заявок на выплату' : 'По вашему запросу ничего не найдено'}
                    </td>
                  </tr>
                ) : (
                  filteredWithdrawals.map((withdrawal, index) => (
                    <motion.tr
                      key={withdrawal._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="py-2">
                        <div>
                          <BlurredAmount 
                             amount={withdrawal.amount}
                             className="text-white font-medium text-sm"
                             formatCurrency={true}
                           />
                          <div className="text-tesla-gray text-xs">
                            <BlurredAmount 
                              amount={`${parseFloat(withdrawal.usdtAmount).toFixed(2)} USDT`}
                              className=""
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3">{getStatusIcon(withdrawal.status)}</div>
                          <span className={`text-xs font-medium ${getStatusColor(withdrawal.status)}`}>
                            {getStatusText(withdrawal.status)}
                          </span>
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="font-mono text-xs bg-gray-800/50 px-1.5 py-0.5 rounded text-tesla-gray">
                          {withdrawal.usdtWallet.slice(0, 6)}...{withdrawal.usdtWallet.slice(-6)}
                        </div>
                      </td>
                      <td className="py-2">
                        <span className="text-tesla-gray text-xs">
                          {formatDate(withdrawal.createdAt)}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="text-tesla-gray text-xs">
                          {withdrawal.processedAt ? formatDate(withdrawal.processedAt) : '—'}
                        </span>
                      </td>
                      <td className="py-2">
                        <div className="flex flex-col space-y-1">
                          {withdrawal.status === 'pending' && (
                            <button
                              onClick={() => handleCancelWithdrawal(withdrawal._id)}
                              className="text-red-400 hover:text-red-300 transition-colors text-xs underline text-left"
                            >
                              Отменить
                            </button>
                          )}

                          {withdrawal.rejectionReason && (
                            <div className="text-xs text-red-400 max-w-xs">
                              <strong>Отклонена:</strong> {withdrawal.rejectionReason.length > 30 ? withdrawal.rejectionReason.slice(0, 30) + '...' : withdrawal.rejectionReason}
                            </div>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {filteredWithdrawals.length === 0 ? (
              <div className="text-center py-6 text-tesla-gray text-sm">
                {withdrawals.length === 0 ? 'Нет заявок на выплату' : 'По вашему запросу ничего не найдено'}
              </div>
            ) : (
              filteredWithdrawals.map((withdrawal, index) => (
                <motion.div
                  key={withdrawal._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="tesla-glass p-3 rounded-lg"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <BlurredAmount 
                         amount={withdrawal.amount}
                         className="text-white font-medium text-base"
                         formatCurrency={true}
                       />
                      <p className="text-tesla-gray text-xs">
                        <BlurredAmount 
                          amount={`${parseFloat(withdrawal.usdtAmount).toFixed(2)} USDT`}
                          className=""
                        />
                      </p>
                      <p className="text-tesla-gray text-xs font-mono">
                        {withdrawal.usdtWallet.slice(0, 6)}...{withdrawal.usdtWallet.slice(-6)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3">{getStatusIcon(withdrawal.status)}</div>
                      <span className={`text-xs font-medium ${getStatusColor(withdrawal.status)}`}>
                        {getStatusText(withdrawal.status)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-tesla-gray">
                    <span>{formatDate(withdrawal.createdAt)}</span>
                    {withdrawal.status === 'pending' && (
                      <button
                        onClick={() => handleCancelWithdrawal(withdrawal._id)}
                        className="text-red-400 hover:text-red-300 transition-colors text-xs"
                      >
                        Отменить
                      </button>
                    )}
                  </div>
                  {withdrawal.rejectionReason && (
                    <div className="mt-1 text-xs text-red-400">
                      <strong>Отклонена:</strong> {withdrawal.rejectionReason.length > 40 ? withdrawal.rejectionReason.slice(0, 40) + '...' : withdrawal.rejectionReason}
                    </div>
                  )}

                </motion.div>
              ))
            )}
          </div>

          {/* Пагинация */}
          {withdrawalStats && withdrawalStats.total > 20 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <button className="tesla-button-secondary text-sm px-3 py-2">
                  Предыдущая
                </button>
                <span className="tesla-button text-sm px-3 py-2">
                  1
                </span>
                <button className="tesla-button-secondary text-sm px-3 py-2">
                  Следующая
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Payments;